import { doc, getDoc, setDoc, increment } from "firebase/firestore";
import { db } from "../firebase";

export const FACTIONS = ["bracer", "ouroboros", "church"];

export const FACTION_NAMES = {
  bracer: "Bracer Guild",
  ouroboros: "Ouroboros",
  church: "Septian Church",
};

export const FACTION_IMAGES = {
  bracer: "/images/Factions/Bracer_Guild_29.webp",
  ouroboros: "/images/Factions/Ouroboros_Logo_29.webp",
  church: "/images/Factions/Septian_Church_-_Crest.webp",
};

const FACTION_POINTS_START = new Date("2026-06-01T00:00:00Z");

export function isFactionPointsActive() {
  return Date.now() >= FACTION_POINTS_START.getTime();
}

export function getCurrentFactionMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function getFactionName(f) {
  return FACTION_NAMES[f] || f;
}

export function getFactionImage(f) {
  return FACTION_IMAGES[f] || null;
}

export function getFactionEmoji(f) {
  return FACTION_EMOJIS[f] || "⚔️";
}

let TOTAL_BADGES = 105;

if (process.env.NODE_ENV === "development") {
  TOTAL_BADGES = 105;
}

export function getFactionRewardBadge(month, faction) {
  if (TOTAL_BADGES > 0) {
    const monthNum = parseInt(month.split("-")[1], 10);
    const year = parseInt(month.split("-")[0], 10);
    const factionIndex = FACTIONS.indexOf(faction);
    const seed = year * 31 + monthNum * 13 + factionIndex * 7;
    const badgeIndex = seed % TOTAL_BADGES;
    return `/images/Factions/reward_badges/badge_${badgeIndex}.webp`;
  }
  return "/images/Factions/reward_badges/Achievement_1_29.webp";
}

export function updateFactionPoints(hasWon, guessesUsed) {
  try {
    if (!isFactionPointsActive()) return;
    const faction = localStorage.getItem("kisekidle-faction");
    if (!faction || !FACTIONS.includes(faction)) return;

    const month = getCurrentFactionMonth();
    const storedMonth = localStorage.getItem("kisekidle-faction-points-month");
    let points = parseInt(localStorage.getItem("kisekidle-faction-points") || "0", 10);

    if (storedMonth !== month) {
      points = 0;
      localStorage.setItem("kisekidle-faction-points-month", month);
    }

    if (hasWon) {
      points += guessesUsed === 1 ? 2 : 1;
    } else {
      points = Math.max(0, points - 1);
    }

    localStorage.setItem("kisekidle-faction-points", String(points));

    const factionRef = doc(db, "factions", month);
    if (hasWon) {
      const earned = guessesUsed === 1 ? 2 : 1;
      setDoc(factionRef, {
        [faction]: increment(earned),
        updatedAt: new Date(),
      }, { merge: true }).catch(() => {});
    } else {
      setDoc(factionRef, {
        [faction]: increment(-1),
        updatedAt: new Date(),
      }, { merge: true }).catch(() => {});
    }

    recordFactionMonth(month, faction);
  } catch (e) {}
}

export function recordFactionMonth(month, faction) {
  try {
    const history = JSON.parse(localStorage.getItem("kisekidle-faction-history") || "{}");
    if (!history[month]) {
      history[month] = faction;
      localStorage.setItem("kisekidle-faction-history", JSON.stringify(history));
    }
  } catch (e) {}
}

const DAILY_GAME_REGEX = /^kisekidle-.+-(\d{4})-(\d{1,2})-(\d{1,2})$/;

export function calculateCorrectUserPoints() {
  try {
    const now = new Date();
    const todayYear = now.getUTCFullYear();
    const todayMonth = now.getUTCMonth() + 1;
    const todayDay = now.getUTCDate();

    const yesterday = new Date(now);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    const yesterdayYear = yesterday.getUTCFullYear();
    const yesterdayMonth = yesterday.getUTCMonth() + 1;
    const yesterdayDay = yesterday.getUTCDate();

    let total = 0, firstGuess = 0, normal = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const m = key.match(DAILY_GAME_REGEX);
      if (!m) continue;
      const y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);

      const isToday = y === todayYear && mo === todayMonth && d === todayDay;
      const isYesterday = y === yesterdayYear && mo === yesterdayMonth && d === yesterdayDay;
      if (!isToday && !isYesterday) continue;

      try {
        const val = JSON.parse(localStorage.getItem(key));
        if (val && typeof val.hasWon === "boolean" && Array.isArray(val.guesses)) {
          total++;
          if (val.hasWon) {
            if (val.guesses.length <= 1) firstGuess++;
            else normal++;
          }
        }
      } catch (e) {}
    }

    if (total === 0) return null;

    const losses = total - normal - firstGuess;
    return { total, normal, firstGuess, losses, correction: firstGuess - losses };
  } catch (e) {
    return null;
  }
}

export async function correctFactionPoints() {
  try {
    if (!isFactionPointsActive()) return;
    const month = getCurrentFactionMonth();
    const flagKey = `kisekidle-faction-corrected-v3-${month}`;
    if (localStorage.getItem(flagKey)) return;

    const faction = localStorage.getItem("kisekidle-faction");
    if (!faction || !FACTIONS.includes(faction)) return;

    const result = calculateCorrectUserPoints();
    if (!result) {
      localStorage.setItem(flagKey, "true");
      return;
    }

    const correctTotal = result.normal + 2 * result.firstGuess - result.losses;
    if (correctTotal === 0) {
      localStorage.setItem(flagKey, "true");
      return;
    }

    const ref = doc(db, "factions", month);
    await setDoc(ref, {
      [faction]: increment(correctTotal),
      updatedAt: new Date(),
    }, { merge: true });

    localStorage.setItem(flagKey, "true");
  } catch (e) {}
}

export function updatePlayerPoints(hasWon, guessesUsed) {
  try {
    const playerId = localStorage.getItem("kisekidle-player-id");
    if (!playerId) return;

    const month = getCurrentFactionMonth();
    const faction = localStorage.getItem("kisekidle-faction");

    const earned = hasWon ? (guessesUsed === 1 ? 2 : 1) : -1;

    const ref = doc(db, "player-scores", playerId);
    setDoc(ref, {
      monthPoints: increment(earned),
      totalPoints: increment(earned),
      month,
      faction: faction || null,
      updatedAt: new Date(),
    }, { merge: true }).catch(() => {});
  } catch (e) {}
}

export async function switchFaction(newFaction) {
  const current = localStorage.getItem("kisekidle-faction");
  if (current === newFaction) return false;

  const month = getCurrentFactionMonth();
  const storedMonth = localStorage.getItem("kisekidle-faction-points-month");
  let points = parseInt(localStorage.getItem("kisekidle-faction-points") || "0", 10);

  if (storedMonth !== month) points = 0;

  const pointsActive = isFactionPointsActive();
  if (pointsActive && points < 50) return false;

  const counts = await getFactionMembersCount(month);
  const limit = getEffectiveSeatLimit(counts);
  if ((counts[newFaction] || 0) >= limit) return false;

  if (current && FACTIONS.includes(current)) {
    recordFactionMonth(month, current);
    await decrementFactionMembers(month, current);
  }

  if (pointsActive) points -= 50;
  localStorage.setItem("kisekidle-faction-points", String(points));
  localStorage.setItem("kisekidle-faction", newFaction);

  recordFactionMonth(month, newFaction);
  await incrementFactionMembers(month, newFaction);
  return true;
}

export async function getFactionLeaderboard(month) {
  try {
    const ref = doc(db, "factions", month);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return {
        bracer: data.bracer || 0,
        ouroboros: data.ouroboros || 0,
        church: data.church || 0,
      };
    }
    return { bracer: 0, ouroboros: 0, church: 0 };
  } catch (e) {
    return { bracer: 0, ouroboros: 0, church: 0 };
  }
}

export function getFactionHistory() {
  try {
    return JSON.parse(localStorage.getItem("kisekidle-faction-history") || "{}");
  } catch (e) {
    return {};
  }
}

export const SEAT_LIMIT_OVERRIDE_ENABLED = false;
export const SEAT_LIMIT_OVERRIDE_VALUE = 46;

export function computeSeatLimit(counts) {
  const total = (counts.bracer || 0) + (counts.ouroboros || 0) + (counts.church || 0);
  return Math.max(20, Math.floor(total * 0.35));
}

export function getEffectiveSeatLimit(counts) {
  if (SEAT_LIMIT_OVERRIDE_ENABLED) {
    return SEAT_LIMIT_OVERRIDE_VALUE;
  }
  return computeSeatLimit(counts);
}

export async function getFactionMembersCount(month) {
  try {
    const ref = doc(db, "factions-members", month);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return {
        bracer: data.bracer || 0,
        ouroboros: data.ouroboros || 0,
        church: data.church || 0,
      };
    }
    return { bracer: 0, ouroboros: 0, church: 0 };
  } catch (e) {
    return { bracer: 0, ouroboros: 0, church: 0 };
  }
}

export async function incrementFactionMembers(month, faction) {
  try {
    const ref = doc(db, "factions-members", month);
    await setDoc(ref, {
      [faction]: increment(1),
      updatedAt: new Date(),
    }, { merge: true });
    return true;
  } catch (e) {
    return false;
  }
}

export async function decrementFactionMembers(month, faction) {
  try {
    const ref = doc(db, "factions-members", month);
    await setDoc(ref, {
      [faction]: increment(-1),
      updatedAt: new Date(),
    }, { merge: true });
    return true;
  } catch (e) {
    return false;
  }
}

export async function ensureCurrentMonthMembers(month) {
  let counts = await getFactionMembersCount(month);

  const [year, m] = month.split("-").map(Number);
  const prevDate = new Date(Date.UTC(year, m - 1, 1));
  prevDate.setUTCMonth(prevDate.getUTCMonth() - 1);
  const prevMonth = `${prevDate.getUTCFullYear()}-${String(prevDate.getUTCMonth() + 1).padStart(2, "0")}`;
  const prevCounts = await getFactionMembersCount(prevMonth);
  const prevTotal = (prevCounts.bracer || 0) + (prevCounts.ouroboros || 0) + (prevCounts.church || 0);
  const curTotal = (counts.bracer || 0) + (counts.ouroboros || 0) + (counts.church || 0);

  if (prevTotal > curTotal) {
    counts = {
      bracer: prevCounts.bracer || 0,
      ouroboros: prevCounts.ouroboros || 0,
      church: prevCounts.church || 0,
    };
  }

  const userFaction = localStorage.getItem("kisekidle-faction");
  if (userFaction && FACTIONS.includes(userFaction) && (counts[userFaction] || 0) === 0) {
    incrementFactionMembers(month, userFaction).catch(() => {});
    counts = { ...counts, [userFaction]: 1 };
  }

  const ref = doc(db, "factions-members", month);
  setDoc(ref, { ...counts, updatedAt: new Date() }, { merge: true }).catch(() => {});

  return counts;
}

export async function canJoinFaction(month, faction) {
  const counts = await getFactionMembersCount(month);
  const limit = getEffectiveSeatLimit(counts);
  return (counts[faction] || 0) < limit;
}
